/* Reversing one decided answer atom.
 *
 * Accepting an atom writes into the stored QuestionnaireResponse, which lives
 * outside the collaborative document the editor's UndoManager tracks, so
 * nothing reverses it on its own. A reversal is therefore a compensating write,
 * planned here as a pure function of the answer as it stands NOW plus the two
 * snapshots the decision took.
 *
 * Only the atom's own line may be rewritten. Between the accept and the undo a
 * sibling atom may have been accepted into another line of the same answer, and
 * undoing one decision must never revert another — so the whole answer is
 * restored only when nothing has been written since, and otherwise a single
 * label line is substituted while every other line stands as it is now.
 *
 * When the atom's own line no longer holds what the decision put there, someone
 * has rewritten it in the meantime and the compensation is REFUSED rather than
 * guessed at. Restoring blindly would clobber a clinician's own later edit, and
 * a refusal that says why is recoverable where a silent wrong write is not. */

import type { Attachment } from '@medplum/fhirtypes'
import type { PersistedQuestionnaireAnswerOp } from '@seen/core/models/compositionNote'
import {
  labelLineValue,
  substituteLabelValue,
} from '#src/components/care-plan/answerLabelLines.ts'

// What an atom addresses within an item's answer. A labelled atom owns one
// scaffold line, optionally qualified by the heading it sits under; an atom
// with no label owns the whole answer.
export type AnswerAtomTarget = Pick<
  PersistedQuestionnaireAnswerOp['answers'][number],
  'label' | 'section'
>

/* The stored value of a questionnaire answer this code can reason about. The
 * care-plan demographic items are text (HTML strings) and booleans; anything
 * else reaching here is treated as no answer, which makes a reversal refuse
 * rather than write a value it does not understand. */
export type AnswerValue = string | boolean | undefined

// The answer as one decision found it, or left it.
export interface AtomAnswerSnapshot {
  // The item's WHOLE stored answer at that moment.
  answer: AnswerValue
  /* The atom's own scaffold line with the label stripped. Null when the answer
   * held no such line — which is the state an accept leaves behind it when it
   * had to append the label — and always null for an atom that owns the whole
   * answer, whose line is the answer itself. */
  line: string | null
}

export type AnswerReversal =
  | { kind: 'write'; value: AnswerValue }
  | { kind: 'refuse'; reason: string }

export const ANSWER_CHANGED_REASON =
  'This answer has been edited since, so the change cannot be undone without overwriting that edit.'

export function toAnswerValue(
  value: string | number | boolean | Attachment | undefined
): AnswerValue {
  return typeof value === 'string' || typeof value === 'boolean'
    ? value
    : undefined
}

export function snapshotAnswerAtom(
  answer: AnswerValue,
  atom: AnswerAtomTarget
): AtomAnswerSnapshot {
  return {
    answer,
    line:
      atom.label && typeof answer === 'string'
        ? labelLineValue(answer, atom.label, atom.section)
        : null,
  }
}

/* The write that moves this atom from the `from` state back to the `to` state,
 * given the answer as stored right now. Undo passes from=after, to=before; redo
 * passes them the other way round, so one planner covers both directions.
 *
 * The whole answer is restored verbatim when it still matches `from` exactly:
 * nothing else has been written since, so a wholesale restore is precise and
 * also removes a line the decision had to append. Once anything else in the
 * answer has moved, only this atom's line may be touched, and only while that
 * line still holds exactly what the decision put there. */
export function planAnswerReversal(
  current: AnswerValue,
  from: AtomAnswerSnapshot,
  to: AtomAnswerSnapshot,
  atom: AnswerAtomTarget
): AnswerReversal {
  if (current === from.answer) {
    return { kind: 'write', value: to.answer }
  }
  if (!atom.label || typeof current !== 'string') {
    return { kind: 'refuse', reason: ANSWER_CHANGED_REASON }
  }
  if (labelLineValue(current, atom.label, atom.section) !== from.line) {
    return { kind: 'refuse', reason: ANSWER_CHANGED_REASON }
  }
  const value = substituteLabelValue(
    current,
    atom.label,
    to.line ?? '',
    atom.section
  )
  // The line the reversal was going to write is gone from the answer, so there
  // is nowhere to put the value back that would not invent a line.
  return value === null
    ? { kind: 'refuse', reason: ANSWER_CHANGED_REASON }
    : { kind: 'write', value }
}
